import dompurify from 'dompurify';
import { stripHtml } from 'string-strip-html';

/**
 * Converts a string to HTML and sanitizes it
 * @param text
 */
export function textToHtml(text: string | null | undefined) {
    if (typeof text !== 'string') return text || '';
    if (!text) {
        return '';
    }
    return dompurify.sanitize(text).replaceAll('\n', '<br/>');
}

/**
 * Converts HTML to text
 * @param html
 */
export function htmlToText(html: string | null | undefined) {
    if (!html) {
        return '';
    }
    return stripHtml(html).result;
}

const LEGACY_SMS_HTML_PREFIX = /^\s*(?:<!doctype html[^>]*>\s*)?<html(?:\s[^>]*)?>/i;
const LEGACY_SMS_HTML_BODY_OPEN = /<body(?:\s[^>]*)?>/i;
const LEGACY_SMS_HTML_SUFFIX = /<\/body>\s*<\/html>\s*$/i;

function isLegacySmsHtmlDocument(content: string): boolean {
    const prefix = LEGACY_SMS_HTML_PREFIX.exec(content);
    if (!prefix) {
        return false;
    }

    const suffix = LEGACY_SMS_HTML_SUFFIX.exec(content);
    if (!suffix) {
        return false;
    }

    return LEGACY_SMS_HTML_BODY_OPEN.test(content.slice(prefix[0].length, suffix.index));
}

/**
 * Converts the full HTML documents used by legacy SMS rows back to readable plain text.
 * Current SMS bodies are already plain text and must stay unchanged so tag-shaped content such as
 * `Reply <YES> to confirm` is not mistaken for markup.
 */
export function smsBodyToText(content: string | null | undefined): string {
    if (!content) {
        return '';
    }
    if (!isLegacySmsHtmlDocument(content)) {
        return content;
    }

    const contentWithLineBreaks = content.replace(/<\/(?:p|div)>|<br\s*\/?>/gi, '\n');
    const doc = new DOMParser().parseFromString(contentWithLineBreaks, 'text/html');
    return (doc.body.textContent ?? '').trim();
}

/**
 * Sanitizes HTML
 * @param html
 */
export function sanitizeHtml(html: string | null | undefined) {
    if (typeof html !== 'string') return html || '';
    if (!html) {
        return '';
    }

    return dompurify.sanitize(html, { ALLOWED_ATTR: ['href', 'target'] });
}

export function htmlContainsText(html: string | null | undefined) {
    if (!html) {
        return false;
    }

    const parser = new DOMParser();
    const { body } = parser.parseFromString(sanitizeHtml(html), 'text/html');

    return !!body.textContent?.trim();
}

/**
 * Sanitizes and normalizes content by removing unnecessary whitespace
 * @param content - The content to sanitize
 * @returns Sanitized and normalized content
 */
export function sanitizeContent(content: string | null | undefined): string {
    if (typeof content !== 'string') return '';
    if (!content) return '';

    // Remove multiple spaces, tabs, and newlines
    return content
        .replace(/[\s\n\r\t]+/g, ' ') // Replace multiple whitespace with single space
        .trim(); // Remove leading and trailing whitespace
}
